import { Account } from './../../model/account';
import 'rxjs/Rx';
import { MiniStorageService } from './mini-storage.service';
import { Injectable } from '@angular/core';
import { AuthorizationProperties, AuthorizationInfo, AppSecurityInfo } from 'src/app/authentication/auth-info';
import { AuthHelper } from 'src/app/authentication/auth-helpers';
import { ServiceUtilsService } from './service-utils.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError } from 'rxjs/operators/catchError';
import { of, Subject, throwError } from 'rxjs';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { ChangePasswordInfo } from 'src/app/authentication/credentials';
import { TranslatedVariables } from 'src/app/commons/translated-variables';
import { OperationResult, TbAuthService } from '@tb/auth';
import { map, tap } from 'rxjs/operators';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { UrlMaker } from 'src/app/commons/url-maker';
import { WorkFlowService } from './work-flow.service';

@Injectable()
// ================================================================================
export class LoginService {
    private storeApiUrl: string;
    private authUrl: string;
    private landingUrlAfterMYZLogin: string;
    private temporaryPassword: string;
    private opRes: OperationResult = new OperationResult();
    jwt: string;
    authorizationStored: string;
    errorMessage = '';
    public logOff$ = new Subject();
    private loggedCAS = new Subject<boolean>();
    currentLoggedCAS = this.loggedCAS.asObservable();

    // --------------------------------------------------------------------------------
    constructor(
        private http: HttpClient,
        private miniStorage: MiniStorageService,
        private authService: TbAuthService,
        private variable: TranslatedVariables,
        private workFlow: WorkFlowService,
        private serviceUtilsService: ServiceUtilsService
    ) {
        this.http = http;
        this.storeApiUrl = environment.settings.storeApiUrl;
        this.authUrl = environment.settings.authUrl;
        this.landingUrlAfterMYZLogin = environment.settings.landingUrlAfterMYZLogin;
    }

    getauthinfo() {
        if (this.authService.isSessionStorage()) {
            this.jwt = sessionStorage.getItem('M4_jwt_token');
        } else {
            this.jwt = localStorage.getItem('M4_jwt_token');
        }
        // se nessuno loginato, rimuovo tutto poi verrà valorizato alla login
        if (this.jwt === null || this.jwt === undefined || this.jwt === 'undefined') {
            localStorage.removeItem('auth-info');
            sessionStorage.removeItem('auth-info');
            return null;
        }
        //altrimenti ho qualcuno loginato, verifico di le informazioni storate
        if (this.authService.isSessionStorage()) {
            this.authorizationStored = sessionStorage.getItem('auth-info');
        } else {
            this.authorizationStored = localStorage.getItem('auth-info');
        }

        // ho delle informazioni storate, verifico siano coerenti con quanto indicato nel token storato dalla lib di login
        if (this.authorizationStored != null && this.authorizationStored != undefined) {
            // account loginato diverso da quello che avevo storato, ripulisco e passo sotto per aggiornare le info, altrimenti ok così
            const authorizationProperties: AuthorizationProperties = JSON.parse(this.authorizationStored);
            if (this.jwt != authorizationProperties.jwtEncoded) {
                localStorage.removeItem('auth-info');
                sessionStorage.removeItem('auth-info');
            } else return this.authorizationStored;
        }
        // login da storare
        let authInfo: AuthorizationInfo;
        const authBag = AuthHelper.GetAuthorizationsFromJWT(this.jwt);

        authInfo = authBag.AuthInfo;
        authInfo.SetRolesFromStorage();
        const appAccessInfo: AppSecurityInfo = authBag.AppInfo;

        this.setminiStorage('app-bag', appAccessInfo);

        if (authInfo != null) this.authorizationStored = JSON.stringify(authInfo.authorizationProperties);

        if (this.authService.isSessionStorage()) {
            // save in sessionstorage all the information about authorization and the account currently connected
            sessionStorage.setItem('auth-info', this.authorizationStored);
        } else {
            // save in localstorage all the information about authorization and the account currently connected
            localStorage.setItem('auth-info', this.authorizationStored);
        }

        return this.authorizationStored;
    }

    setminiStorage(key: string, val: object) {
        this.miniStorage.set(key, val);
    }

    getAuthInfoProperties(): AuthorizationProperties {
        var authInfo: AuthorizationProperties;
        authInfo = JSON.parse(this.getauthinfo());
        if (authInfo === undefined || authInfo === null) authInfo = new AuthorizationProperties();
        return authInfo;
    }

    // --------------------------------------------------------------------------------
    getAccountIfExistsBy(accountName: string) {
        if (accountName === '') {
            return throwError('The model name to query is missing!');
        }

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('accounts').appendSegment(accountName).toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => ErrorObservable.create(error || 'Server error')));
    }

    async getAccountToPromiseBy(accountName: string): Promise<Account> {
        if (accountName === '') {
            alert('The account name is missing!');
            return;
        }

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('accounts').appendSegment(accountName).toString();

        return this.http
            .get<Account>(url, { headers: headers })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    let errorDetail = error.message;

                    if (error.status === 401) {
                        return throwError('Unauthorized: ' + errorDetail);
                    }
                    if (error.status === 403) {
                        return throwError('Forbidden resource: ' + errorDetail);
                    }

                    return throwError(errorDetail || 'server error (getAccountToPromiseBy)');
                })
            )
            .toPromise();
    }

    getAccountToObservable(accountName: string): Observable<Account> {
        if (accountName === '') {
            alert('The account name is missing!');
            return;
        }

        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('accounts').appendSegment(accountName).toString();

        return this.http
            .get<Account>(url, { headers: headers })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    let errorDetail = error.message;

                    if (error.status === 401) {
                        return throwError('Unauthorized: ' + errorDetail);
                    }
                    if (error.status === 403) {
                        return throwError('Forbidden resource: ' + errorDetail);
                    }

                    return throwError(errorDetail || 'server error (getAccountToPromiseBy)');
                })
            )
    }


    changePassword(body: ChangePasswordInfo): Observable<OperationResult> {
        let bodyString = JSON.stringify(body);
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('changepassword').toString();

        return this.http
            .post<OperationResult>(url, bodyString, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => ErrorObservable.create(error || 'Error on changePassword')));
    }

    // reset account password quando sei già dentro lo store
    resetPassword(accountName: string): Observable<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('resetPassword').appendSegment(accountName).toString();
        return this.http
            .post<OperationResult>(url, '', { headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in resetPassword')));
    }

    resendMail(accountName: string): Observable<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('reactivateaccount').appendSegment(accountName).toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in resendMail')));
    }

    // // hai dimenticato la password ?
    // forgetPassword(accountName: string): Observable<OperationResult> {
    //     let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    //     return this.http
    //         .post<OperationResult>(this.microLoginUrl + 'resetPassword' + '/' + accountName, '', { headers })
    //         .pipe(catchError((error : HttpErrorResponse) => throwError(error || 'Server error in forgetPassword')));
    // }

    // hai dimenticato le credenziali ?
    credentialsRecovery(accountName: string): Observable<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('credentialrecovery')
            .appendSegment(accountName)
            .toString();

        return this.http
            .post<OperationResult>(url, '', { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Server error in credentialsRecovery')));
    }

    newAccountValidation(operationToken: string): Observable<OperationResult> {
        if (operationToken === '' || operationToken === undefined) {
            alert('operationToken is missing! (newAccountValidation)');
            return;
        }
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const jobj = JSON.stringify(operationToken);
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('newaccountvalidation').toString();
        return this.http
            .post<OperationResult>(url, jobj, { headers: headers })
            .pipe(catchError((error: HttpErrorResponse) => throwError(error || 'Error on newAccountValidation')));
    }

    setTemporaryPassword(temporaryPassword: string) {
        this.temporaryPassword = temporaryPassword;
    }

    getTemporaryPassword() {
        return this.temporaryPassword;
    }

    login(loginRequest: LoginRequest): Promise<LoginResponse> {
        return this.http
            .post<LoginResponse>(this.storeApiUrl + 'login/', loginRequest)
            .pipe(
                map((loginResponse: LoginResponse) => {
                    if (!loginResponse.Result) {
                        this.clearStorage();

                        if (loginResponse.ResultCode === 58) {
                            console.log('AuthService: Account Locked' + loginResponse.Message);
                            loginResponse.Message = this.getLockedUserMessage(loginResponse.Message);
                        }
                        loginResponse.Message = loginResponse.Message ? loginResponse.Message : 'Login error...';
                        this.errorMessage = loginResponse.Message;
                        return loginResponse;
                    }

                    this.storageData(loginResponse);

                    return loginResponse;
                })
            )
            .toPromise();
    }

    // ---------------------------------------------------------------------------
    getLockedUserMessage(messageFromLogin: string) {
        if (!messageFromLogin) return messageFromLogin;
        const seconds: number = +messageFromLogin;
        let msg = messageFromLogin;
        if (isNaN(seconds)) return msg;
        if (seconds < 60 && seconds > -1)
            msg = `${this.variable.loginlocked} ${this.variable.loginlocked_tryagain} ${seconds} ${this.variable.loginlocked_seconds}`;
        else if (seconds >= 60) {
            const minVal = Math.round(seconds / 60);
            msg =
                minVal === 1
                    ? `${this.variable.loginlocked} ${this.variable.loginlocked_tryagain} ${this.variable.loginlocked_minute}`
                    : `${this.variable.loginlocked} ${this.variable.loginlocked_tryagain} ${minVal} ${this.variable.loginlocked_minutes}`;
        }
        return msg;
    }

    public clearStorage() {
        sessionStorage.removeItem(StorageVars.JWT);
        sessionStorage.removeItem(StorageVars.CULTURE);
        sessionStorage.removeItem(StorageVars.UI_CULTURE);
        sessionStorage.removeItem(StorageVars.ACCOUNT_ROLES);
        //sessionStorage.removeItem("SHOPPINGCOMPACT_LIST");

        localStorage.removeItem(StorageVars.JWT);
        localStorage.removeItem(StorageVars.CULTURE);
        localStorage.removeItem(StorageVars.UI_CULTURE);
        localStorage.removeItem(StorageVars.ACCOUNT_ROLES);
        //localStorage.removeItem("SHOPPINGCOMPACT_LIST");
    }

    private storageData(loginResponse: LoginResponse) {
        const respCulture: string =
            loginResponse.RegionalSettings === undefined || loginResponse.RegionalSettings.length === 0
                ? window.navigator.language
                : loginResponse.RegionalSettings;
        const respUiCulture: string =
            loginResponse.Language === undefined || loginResponse.Language.length === 0
                ? window.navigator.language
                : loginResponse.Language;

        if (environment.auth.sessionStorage) {
            sessionStorage.setItem(StorageVars.JWT, loginResponse.JwtToken);
            sessionStorage.setItem(StorageVars.CULTURE, respCulture);
            sessionStorage.setItem(StorageVars.UI_CULTURE, respUiCulture);
            sessionStorage.setItem(StorageVars.ACCOUNT_ROLES, JSON.stringify(loginResponse.Roles));

            if (loginResponse.AccountName) sessionStorage.setItem(StorageVars.ACCOUNT_NAME, loginResponse.AccountName);
            if (loginResponse.SubscriptionKey) sessionStorage.setItem(StorageVars.SUBSCRIPTION, loginResponse.SubscriptionKey);
            if (loginResponse.SubscriptionDesc)
                sessionStorage.setItem(StorageVars.SUBSCRIPTION_DESCRIPTION, loginResponse.SubscriptionDesc);
        } else {
            localStorage.setItem(StorageVars.JWT, loginResponse.JwtToken);
            localStorage.setItem(StorageVars.CULTURE, respCulture);
            localStorage.setItem(StorageVars.UI_CULTURE, respUiCulture);
            localStorage.setItem(StorageVars.ACCOUNT_ROLES, JSON.stringify(loginResponse.Roles));

            if (loginResponse.AccountName) localStorage.setItem(StorageVars.ACCOUNT_NAME, loginResponse.AccountName);
            if (loginResponse.SubscriptionKey) localStorage.setItem(StorageVars.SUBSCRIPTION, loginResponse.SubscriptionKey);
            if (loginResponse.SubscriptionDesc) localStorage.setItem(StorageVars.SUBSCRIPTION_DESCRIPTION, loginResponse.SubscriptionDesc);
        }
    }

    async resendOTPV3(accname: string, processid: string, twoFactorType: number): Promise<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('resendotp_v3')
            .appendSegment(accname)
            .appendSegment(processid)
            .appendSegment(twoFactorType)
            .toString();

        let sendMessage;
        let failedSendMessage;
        switch (twoFactorType) {
            case 1:
                sendMessage = this.variable.smsSent;
                failedSendMessage = this.variable.smsNotSent;
                break;
            case 2:
                sendMessage = this.variable.mailSent;
                failedSendMessage = this.variable.mailNotSent;
                break;
        }

        return this.http
            .post<OperationResult>(url, { headers })
            .pipe(
                map((res) => {
                    if (!res || !res.Result) {
                        res.Code = 663;
                        this.workFlow.showSnackBar(this.variable.warningTitle, failedSendMessage);
                        return;
                    }
                    this.workFlow.showSnackBar(sendMessage);
                    return res;
                }),
                catchError((error: HttpErrorResponse) => {
                    console.error(`Error Code: ${error.status}.\nMessage: ${error.message}`);
                    const res = new OperationResult();
                    res.Message = `Error Code: ${error.status}.\nMessage: ${error.message}`;
                    res.Code = 669;
                    this.workFlow.showSnackBar(this.variable.warningTitle, error.message);
                    return of(res);
                })
            )
            .toPromise();
    }

    async resendOTP(accname: string): Promise<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker().appendBaseUrl(this.storeApiUrl).appendSegment('resendotp').appendSegment(accname).toString();

        return this.http
            .post<OperationResult>(url, { headers })
            .pipe(
                map((res) => {
                    if (!res || !res.Result) {
                        res.Code = 663;
                        this.workFlow.showSnackBar(this.variable.warningTitle, res.Message);
                        return;
                    }
                    let codeSent = this.workFlow.languageIsItalian()
                        ? 'Codice inviato alla modalità impostata'
                        : 'Code sent to the set mode';
                    this.workFlow.showSnackBar(codeSent);
                    return res;
                }),
                catchError((error: HttpErrorResponse) => {
                    console.error(`Error Code: ${error.status}.\nMessage: ${error.message}`);
                    const res = new OperationResult();
                    res.Message = `Error Code: ${error.status}.\nMessage: ${error.message}`;
                    res.Code = 669;
                    this.workFlow.showSnackBar(this.variable.warningTitle, error.message);
                    return of(res);
                })
            )
            .toPromise();
    }

    setCASLoggedIn() {
      this.loggedCAS.next(true);
    }

    setCASLoggedOut() {
      this.loggedCAS.next(true);
    }

    loginMYZ(): void {
      //window.location.href = `${this.authUrl}/login?returnUrl=${this.landingUrlAfterMYZLogin}`;
      window.location.href = `${this.authUrl}/login`;
    }

    logoutMYZ(): void {
      window.open(
        `${this.authUrl}/logout`,
        '_blank'
      );
    }

    getCASUserId(): Observable<any> {
      return this.http.get(`${this.authUrl}/getcasuserid`, { withCredentials: true });
    }

    getCASCompanyInfo(ssoid = ''): Observable<any> {
      const url = new UrlMaker()
        .appendBaseUrl(this.storeApiUrl)
        .appendSegment('getcompanyinfo')
        .appendParameter('ssoid', ssoid)
        .toString();

      return this.http.post(url, { withCredentials: true });
    }

    CASAccountInit(loginRete: string, email: string, ssoid: string): Observable<any> {
      const url = new UrlMaker()
        .appendBaseUrl(this.storeApiUrl)
        .appendSegment('casaccountinit')
        .appendParameter('CASUserName', loginRete)
        .appendParameter('email', email)
        .appendParameter('ssoid', ssoid)
        .toString();

      return this.http.post(url, { withCredentials: true });
    }

    CASAccountLogin(loginRete: string, jwtToken: string): Observable<any> {
      const url = new UrlMaker()
        .appendBaseUrl(this.storeApiUrl)
        .appendSegment('casaccountlogin')
        .appendParameter('CASUserName', loginRete)
        .appendParameter('JWT', jwtToken)
        .toString();

      return this.http.post(url, { withCredentials: true })
        .pipe(
          map((loginResponse: LoginResponse) => {
              if (!loginResponse.Result) {
                  this.clearStorage();

                  if (loginResponse.ResultCode === 58) {
                      console.log('AuthService: Account Locked' + loginResponse.Message);
                      loginResponse.Message = this.getLockedUserMessage(loginResponse.Message);
                  }
                  loginResponse.Message = loginResponse.Message ? loginResponse.Message : 'Login error...';
                  this.errorMessage = loginResponse.Message;
                  return loginResponse;
              }

              this.storageData(loginResponse);
              return loginResponse;
          })
      );
    }
}

export class StorageVars {
    public static JWT = 'M4_jwt_token';
    public static CULTURE = 'M4_culture';
    public static UI_CULTURE = 'M4_ui_culture';
    public static ACCOUNT_NAME = 'M4_account_name';
    public static ACCOUNT_ROLES = 'M4_account_roles';
    public static SUBSCRIPTION = 'M4_subscription';
    public static SUBSCRIPTION_DESCRIPTION = 'M4_subscription_desc';
}

export interface LoginResponse {
    Result: boolean;
    Message: string;
    ResultCode: number;
    JwtToken: string;
    ExpirationDate: string;
    RegionalSettings: string;
    Language: string;
    Roles: string;
    AccountName: string;
    Email: string;
    SubscriptionKey: string;
    SubscriptionDesc: string;
}

export class LoginRequest {
    token = ''; // se presente, sto facendo una login con token, non ci sará il check password
    appId = 'M4'; // identificativo dell'applicazione che sta effettuando la login (da tabellare)
    accountName = '';
    password = '';
    subscriptionKey!: string; // login su una specifica Subscription, a seconda dell'appid potrebbe non essere presente
    processID: string;// codice identificativo dello specifico processo di login, deve essere nuovo ogni volta che viene  fatta la login, un random di 6 numeri va benissimo
    otPassword: string;// otp, se presente
    overwriteLogin = false; // serve per gestire il warning in caso l utente sia gia loginato con la stessa  sub/appid, nel dubbio lasciare false.
    version = 3;//La versione deve essere fino a contrordine  =3.
}

export interface LoginTypeRequest {
    valueType: string;
    descriptionType: string;
    checked: boolean;
}
